<template>
  <div class="overflow-hidden pa-4 main-content w-100" style="height: calc(100vh - 23vh)">
    <v-row class="ma-0 fill-height main-content-row" v-if="!canvasStore.canvasError">
      <v-col cols="6" class="pa-2 h-100">
        <v-sheet
          class="pa-4 pt-0 border fill-height rounded overflow-y-auto overflow-x-hidden"
          elevation="1"
        >
          <!-- Header -->
          <v-row justify="space-between" class="position-sticky top-0 pt-1 bg-white header">
            <v-col cols="auto" class="pt-2 text-start text-h6 font-weight-medium">
              {{ t('messages.creativeEditor') }}
            </v-col>
            <v-col cols="auto" class="pt-1" v-if="!editorStore.isAutoFix">
              <v-switch
                v-model="enableAdvancedSettings"
                density="compact"
                color="primary"
                :label="t('labels.advancedSettings')"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Logo -->
          <v-row class="mt-3">
            <v-col
              cols="12"
              class="text-subtitle-2 text-medium-emphasis text-start font-weight-bold pb-0"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="text-black">{{ t('labels.logo') }} </span>
                <v-btn
                  v-if="brandStore.logos.length > 1 && !editorStore.isAutoFix"
                  width="28"
                  height="28"
                  size="x-small"
                  rounded-sm
                  variant="tonal"
                  class="ml-1 opacity-1"
                  :color="editorStore.isLogoLocked ? 'primary' : 'black'"
                  @click="editorStore.toggleLogoLocked()"
                >
                  <v-icon size="20">
                    {{ editorStore.isLogoLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                  </v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" class="pt-0">
              <SlideGroupSelector
                :logoItems="brandStore.logos"
                v-model:selectedIndex="selectedLogoIndex"
                type="logo"
              />
            </v-col>
          </v-row>

          <!-- Products -->
          <ProductEditor v-if="!editorStore.isAutoFix" @load-canvas="loadCanvasData" />

          <!-- Discount -->
          <v-row class="mt-0" align="center">
            <v-col
              cols="12"
              class="text-subtitle-2 text-medium-emphasis text-start pb-2 font-weight-bold"
            >
              <span class="text-black">Discount</span>
            </v-col>

            <v-col cols="12" class="pt-0">
              <v-menu
                :close-on-content-click="false"
                v-model="discountMenu"
                @update:model-value="onDiscountMenuToggle"
              >
                <template #activator="{ props }">
                  <v-text-field
                    v-bind="props"
                    v-model="discountSummary"
                    density="compact"
                    variant="outlined"
                    readonly
                    hide-details
                    style="background-color: #e4e4e4"
                    :disabled="isShufflingPlacement || canvasStore.canvasLoader"
                  >
                    <template #append-inner>
                      <v-icon color="primary">mdi-pencil</v-icon>
                    </template>
                  </v-text-field>
                </template>

                <v-card width="600" elevation="5">
                  <v-card-text>
                    <v-row class="mt-1">
                      <v-col cols="4" class="pt-0">
                        <v-autocomplete
                          v-model="tempDiscountDetails.type"
                          :items="Object.values(discountTypesInfo)"
                          item-title="label"
                          item-value="value"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                        />
                      </v-col>

                      <v-col
                        cols="4"
                        class="pt-0"
                        v-if="tempDiscountDetails.type != discountTypes.none"
                      >
                        <v-btn-toggle
                          v-model="tempDiscountDetails.variation"
                          divided
                          mandatory
                          variant="outlined"
                        >
                          <v-btn
                            v-for="option in Object.values(discountVariationsInfo)"
                            :key="option.value"
                            :value="option.value"
                            variant="outlined"
                          >
                            {{ option.label }}
                          </v-btn>
                        </v-btn-toggle>
                      </v-col>

                      <v-col
                        cols="4"
                        class="pt-0"
                        v-if="tempDiscountDetails.type != discountTypes.none"
                      >
                        <v-text-field
                          v-model="tempDiscountDetails.value"
                          variant="outlined"
                          density="comfortable"
                          placeholder="Discount"
                          type="number"
                          hide-details="auto"
                          :rules="discountValueRules"
                          min="0"
                          :max="tempDiscountDetails.type == discountTypes.percentage ? 100 : null"
                          @keypress="blockNegativeSign"
                          ref="discountValueMenuField"
                        >
                          <template #append-inner>
                            <v-icon
                              v-if="tempDiscountDetails.type == discountTypes.percentage"
                              icon="mdi-percent"
                            />
                            <span v-else class="currency-symbol">
                              {{ appStore.profileCurrencySymbol }}
                            </span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <v-row v-if="tempDiscountDetails.type != discountTypes.none">
                      <v-col cols="12" class="text-subtitle-2 text-medium-emphasis text-start pb-0">
                        {{ t('labels.discountCode') }}
                      </v-col>
                      <v-col cols="12" class="pt-0">
                        <v-text-field
                          v-model="tempDiscountCouponCode"
                          density="comfortable"
                          variant="outlined"
                          hide-details="auto"
                          :placeholder="t('messages.enterDiscountCode')"
                          counter
                          maxlength="10"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row class="justify-end">
                      <v-col cols="auto">
                        <v-btn variant="outlined" @click="closeDiscountMenu">Cancel</v-btn>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn
                          variant="outlined"
                          color="primary"
                          @click="updateDiscountChanges"
                          :loading="isLoadingHeadlinesUsingDiscount"
                        >
                          Update
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Headline -->
          <v-row class="mt-0">
            <v-col
              cols="12"
              class="text-subtitle-2 text-medium-emphasis text-start pb-2 font-weight-bold"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="text-black">{{ t('labels.headline') }}</span>
                <v-btn
                  v-if="!editorStore.isAutoFix"
                  width="28"
                  height="28"
                  size="x-small"
                  rounded-sm
                  variant="tonal"
                  class="ml-1 opacity-1"
                  :color="editorStore.isHeadlineLocked ? 'primary' : 'black'"
                  @click="editorStore.toggleHeadlineLocked()"
                >
                  <v-icon size="20">
                    {{ editorStore.isHeadlineLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                  </v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" class="pt-0">
              <v-text-field
                v-model="editorStore.headline"
                density="comfortable"
                variant="outlined"
                hide-details="auto"
                placeholder="Enter headline text"
                color="primary"
                class="headline-field"
                :class="{ 'warning-state': isHeadlineWarning }"
                :rules="headlineRules"
                @update:model-value="debouncedUpdateHeadlineText($event?.slice(0, maxLength))"
                counter
                persistent-counter
                :maxlength="maxLength"
                ref="headlineField"
                style="margin-bottom: -10px"
                :error="!isHeadlineValid"
                :disabled="
                  writingHeadlineUsingAI ||
                  isLoadingHeadlinesUsingDiscount ||
                  writingSubheadlineUsingAI ||
                  isShufflingPlacement ||
                  canvasStore.canvasLoader
                "
              >
                <template #message="{ message }">
                  <span :class="isHeadlineWarning ? 'text-warning' : 'text-error'">
                    {{ message }}
                  </span>
                </template>
                <template #append-inner>
                  <v-menu :close-on-content-click="false" v-model="headlinePromptMenu">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-creation"
                        variant="text"
                        color="primary"
                        @mousedown.stop
                      ></v-btn>
                    </template>
                    <v-card width="400" elevation="5" class="prompt-card">
                      <v-card-title class="text-subtitle-1">{{ t('labels.prompt') }}</v-card-title>
                      <v-card-text>
                        <v-textarea
                          v-model="headlinePrompt"
                          variant="outlined"
                          class="text-area"
                          ref="headlinePromptRef"
                          no-resize
                          rows="3"
                          hide-details
                          density="compact"
                          :placeholder="t('labels.writeUsingAI')"
                        >
                          <template #append-inner>
                            <v-btn
                              icon="mdi-auto-fix"
                              variant="outlined"
                              size="small"
                              rounded="lg"
                              color="primary"
                              :loading="writingHeadlineUsingAI"
                              :disabled="
                                writingHeadlineUsingAI ||
                                !headlinePrompt ||
                                headlinePrompt.trim().toLowerCase() ==
                                  editorStore.recentHeadlinePrompt.trim().toLowerCase()
                              "
                              @click="writeHeadlineUsingAI(headlinePrompt)"
                            ></v-btn>
                          </template>
                        </v-textarea>
                        <v-container
                          v-if="canvasStore.aiGeneratedHeadlines.length"
                          class="pa-0 pt-2"
                        >
                          <v-btn
                            v-for="headline in canvasStore.aiGeneratedHeadlines"
                            :key="headline"
                            class="justify-start px-2 text-none headline_outputs"
                            variant="text"
                            size="large"
                            density="comfortable"
                            block
                            @click="updateHeadline(headline, TEXT_TYPE_NAMES.HEADLINE)"
                          >
                            {{ headline }}
                          </v-btn>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Subheadline -->
          <v-row class="mt-0" v-if="showSubheadline">
            <v-col
              cols="12"
              class="text-subtitle-2 text-medium-emphasis text-start pb-2 font-weight-bold"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="text-black">{{ t('labels.subHeadline') }}</span>
                <v-btn
                  v-if="!editorStore.isAutoFix"
                  width="28"
                  height="28"
                  size="x-small"
                  rounded-sm
                  variant="tonal"
                  class="ml-1 opacity-1"
                  :color="editorStore.isSubheadlineLocked ? 'primary' : 'black'"
                  @click="editorStore.toggleSubheadlineLocked()"
                >
                  <v-icon size="20">
                    {{ editorStore.isSubheadlineLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                  </v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" class="pt-0">
              <v-text-field
                v-model="editorStore.subheadline"
                density="comfortable"
                variant="outlined"
                :hide-details="false"
                placeholder="Enter sub-headline text"
                :rules="subheadlineRules"
                class="headline-field"
                :class="{ 'warning-state': isSubheadlineWarning }"
                ref="subheadlineField"
                @update:model-value="
                  debouncedUpdateSubheadlineText($event.slice(0, maxSubheadlineLength))
                "
                counter
                persistent-counter
                :maxlength="maxSubheadlineLength"
                style="margin-bottom: -10px"
                :error="!isSubheadlineValid"
                :disabled="
                  writingSubheadlineUsingAI ||
                  isLoadingHeadlinesUsingDiscount ||
                  writingHeadlineUsingAI ||
                  isShufflingPlacement ||
                  canvasStore.canvasLoader
                "
              >
                <template #message="{ message }">
                  <span :class="isSubheadlineWarning ? 'text-warning' : 'text-error'">
                    {{ message }}
                  </span>
                </template>
                <template #append-inner>
                  <template v-if="!isRegenerating">
                    <v-menu :close-on-content-click="false" v-model="subheadlinePromptMenu">
                      <template #activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon="mdi-creation"
                          variant="text"
                          color="primary"
                          @mousedown.stop
                        ></v-btn>
                      </template>
                      <v-card width="400" elevation="5" class="prompt-card">
                        <v-card-title class="text-subtitle-1"
                          >{{ t('labels.prompt') }}
                        </v-card-title>
                        <v-card-text>
                          <v-textarea
                            v-model="subHeadlinePrompt"
                            variant="outlined"
                            class="text-area"
                            ref="subHeadlinePromptRef"
                            no-resize
                            rows="3"
                            hide-details
                            density="compact"
                            :placeholder="t('labels.writeSubheadlineUsingAI')"
                          >
                            <template #append-inner>
                              <v-btn
                                icon="mdi-auto-fix"
                                variant="outlined"
                                size="small"
                                rounded="lg"
                                color="primary"
                                :loading="writingSubheadlineUsingAI"
                                :disabled="
                                  writingSubheadlineUsingAI ||
                                  !subHeadlinePrompt ||
                                  subHeadlinePrompt.trim().toLowerCase() ==
                                    editorStore.recentSubHeadlinePrompt.trim().toLowerCase()
                                "
                                @click="writeSubHeadlineUsingAI(subHeadlinePrompt)"
                              ></v-btn>
                            </template>
                          </v-textarea>
                          <v-container
                            v-if="canvasStore.aiGeneratedSubHeadlines.length"
                            class="pa-0 pt-2"
                          >
                            <v-btn
                              v-for="subHeadline in canvasStore.aiGeneratedSubHeadlines"
                              :key="subHeadline"
                              class="justify-start px-2 text-none headline_outputs"
                              variant="text"
                              size="large"
                              density="comfortable"
                              block
                              @click="updateHeadline(subHeadline, TEXT_TYPE_NAMES.SUBHEADLINE)"
                            >
                              {{ subHeadline }}
                            </v-btn>
                          </v-container>
                        </v-card-text>
                      </v-card>
                    </v-menu>
                  </template>
                  <v-progress-circular
                    v-else
                    class="mr-2"
                    indeterminate
                    size="24"
                    color="primary"
                  ></v-progress-circular>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Background -->
          <v-container class="px-0 py-0" v-if="showEnhancingOptions && !editorStore.isAutoFix">
            <v-row class="mt-0">
              <v-col
                cols="12"
                class="d-flex align-center text-subtitle-2 text-medium-emphasis text-start font-weight-bold pb-2"
              >
                <span v-if="editorStore.selectedBackgroundOption === 0" class="text-black">
                  {{ t('labels.background') }}
                </span>
                <span v-else class="text-black">{{ t('labels.backdrop') }}</span>
                <v-btn-toggle
                  v-if="aiBackgroundImages.length > 0"
                  color="primary"
                  variant="tonal"
                  density="compact"
                  class="pl-2 pr-4"
                  group
                  mandatory
                  :model-value="editorStore.selectedBackgroundOption"
                  :disabled="isToggleDisabled"
                >
                  <v-btn
                    icon="mdi-image-outline"
                    width="36"
                    @click="editorService.updateBackgroundOption(0)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-format-color-fill"
                    width="36"
                    class="pt-1"
                    @click="editorService.updateBackgroundOption(1)"
                  ></v-btn>
                </v-btn-toggle>
                <v-container class="flex-grow pa-0 ma-0 text-end align-self-baseline">
                  <v-btn
                    width="36"
                    height="36"
                    size="x-small"
                    rounded-sm
                    variant="tonal"
                    class="ml-1 opacity-1"
                    :color="editorStore.isBackgroundLocked ? 'primary' : 'black'"
                    @click="editorStore.toggleBackgroundLocked()"
                  >
                    <v-icon size="20">
                      {{ editorStore.isBackgroundLocked ? 'mdi-lock' : 'mdi-lock-open-variant' }}
                    </v-icon>
                  </v-btn>
                </v-container>
              </v-col>
              <v-col cols="12" class="pt-0">
                <v-chip-group>
                  <v-menu
                    v-if="enableAdvancedSettings"
                    transition="scale-transition"
                    location="bottom"
                    v-model="colorMenu"
                    :close-on-content-click="false"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        variant="outlined"
                        icon="mdi-plus"
                        density="comfortable"
                        class="my-1 mr-3"
                        v-bind="props"
                      ></v-btn>
                    </template>
                    <MenuOption
                      :selectedBackdropOption="editorStore.selectedBackgroundOption"
                      :newBackgroundColor="'#000000'"
                      :newTextColor="
                        editorStore.selectedBackgroundOption === 1 ? '#ffffff' : '#000000'
                      "
                      :colorCode="0"
                      @addBackdrop="addNewBackDrop"
                      @closeMenu="closeMainMenu"
                    />
                  </v-menu>
                  <v-row v-if="editorStore.selectedBackgroundOption === 1">
                    <template v-for="(item, index) in backDrops" :key="index">
                      <v-col v-if="!hiddenBackDropIndexes.includes(index)" cols="auto" class="pr-0">
                        <v-card
                          variant="outlined"
                          :model-value="editorStore.selectedBackDropIndex"
                          :class="{
                            'border-md border-opacity-100 border-primary':
                              editorStore.selectedBackDropIndex === index,
                          }"
                          @click.stop="
                            selectNewBackDrop(
                              editorStore.selectedBackDropIndex,
                              index,
                              true,
                              $event,
                            )
                          "
                          :disabled="isShufflingPlacement || canvasStore.canvasLoader"
                          :id="'backdrop' + index"
                        >
                          <v-row no-gutters>
                            <v-col class="my-1 ml-1">
                              <v-btn
                                :color="item.backgroundColor"
                                size="36"
                                class="text-capitalize font-weight-bold"
                                flat
                                readonly
                                ><span :style="{ color: item.textColor }">Aa</span>
                              </v-btn>
                            </v-col>
                            <v-col class="ma-1">
                              <v-card variant="outlined">
                                <v-img
                                  :src="backgroundImages[item.backgroundTextureIndex]"
                                  width="54"
                                  height="34"
                                  contain
                                  crossorigin="anonymous"
                                ></v-img>
                              </v-card>
                            </v-col>
                          </v-row>
                        </v-card>
                      </v-col>
                    </template>
                    <v-menu
                      transition="scale-transition"
                      location="top"
                      :close-on-content-click="false"
                      v-model="editMenu"
                      :activator="selectedBackdrop"
                    >
                      <MenuOption
                        :selectedBackdropOption="editorStore.selectedBackgroundOption"
                        :newBackgroundColor="brandStore.selectedColor"
                        :newTextColor="canvasStore.textColor"
                        :textureIndex="selectedTextureIndex"
                        :edit="true"
                        @updateBackdrop="editBackDrop"
                        @close-menu="closeMenu"
                      />
                    </v-menu>
                  </v-row>
                  <v-row v-if="editorStore.selectedBackgroundOption === 0">
                    <v-col
                      v-for="(item, index) in allBackGrounds"
                      :key="index"
                      cols="auto"
                      class="pr-0"
                    >
                      <v-card
                        variant="outlined"
                        :model-value="editorStore.selectedBackGroundIndex"
                        :class="{
                          'border-md border-opacity-100 border-primary':
                            editorStore.selectedBackGroundIndex === index,
                        }"
                        @click.stop="selectNewBackGround(index, true)"
                        :disabled="isShufflingPlacement || canvasStore.canvasLoader"
                        :id="'background' + index"
                      >
                        <v-row no-gutters>
                          <v-col class="ma-1">
                            <v-card>
                              <v-img
                                :src="aiBackgroundImages[item.backgroundImageIndex]"
                                width="54"
                                height="34"
                                contain
                                crossorigin="anonymous"
                              ></v-img>
                            </v-card>
                          </v-col>
                          <v-col class="ma-1">
                            <v-card>
                              <div style="width: 54px; height: 34px; background-color: white">
                                <img
                                  :src="
                                    item.backgroundTextureIndex !== null
                                      ? overlays[item.backgroundTextureIndex]
                                      : undefined
                                  "
                                  width="54"
                                  height="34"
                                  contain
                                  crossorigin="anonymous"
                                  :style="
                                    item.backgroundTextureIndex != 0 ? { filter: 'invert(1)' } : ''
                                  "
                                  @error="onImageError"
                                  @load="onImageLoad"
                                />
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                    <v-menu
                      transition="scale-transition"
                      location="top"
                      :close-on-content-click="false"
                      v-model="editMenuBackground"
                      :activator="selectedBackground"
                    >
                      <MenuOption
                        :selectedBackdropOption="editorStore.selectedBackgroundOption"
                        :newBackgroundColor="brandStore.selectedColor"
                        :newTextColor="canvasStore.textColor"
                        :imageIndex="selectedImageIndex"
                        :textureIndex="selectedOverlayIndex"
                        :edit="true"
                        @closeMenu="closeBackgroundMenu"
                        @updateBackground="editBackground"
                      />
                    </v-menu>
                  </v-row>
                </v-chip-group>
              </v-col>
            </v-row>
            <v-row
              class="mt-0"
              v-if="
                placementStore.selectedPlacementOption === PLACEMENT_TYPES.TOP &&
                !editorStore.isAutoFix
              "
            >
              <v-col
                cols="12"
                class="text-subtitle-2 text-medium-emphasis text-start pb-1 font-weight-bold"
              >
                <span class="text-black">{{ $t('labels.enhacements') }}</span>
              </v-col>
              <v-row class="mx-2 mb-2" no-gutters>
                <v-col
                  class="pt-0 px-0"
                  cols="auto"
                  v-for="(item, index) in enhancingItems"
                  :key="index"
                >
                  <v-chip
                    :color="selectedItem === index ? 'primary' : ''"
                    variant="outlined"
                    class="text-body-2 ma-1"
                    @click="selectedItem = selectedItem === index ? null : index"
                    :disabled="isShufflingPlacement || canvasStore.canvasLoader"
                  >
                    <template v-if="enhanceLoading && selectedItem === index">
                      <v-progress-circular
                        indeterminate
                        size="16"
                        width="2"
                        color="primary"
                        class="mr-1"
                      />
                      {{ item.name }}
                    </template>
                    <template v-else>
                      {{ item.name }}
                    </template>
                  </v-chip>
                </v-col>
                <v-col class="pt-0 align-self-center" cols="auto">
                  <v-icon
                    @click="showAddEnhancement = true"
                    class="mx-1 cursor-pointer"
                    style="border: 1px solid black; border-radius: 50%"
                  >
                    mdi-plus
                  </v-icon>
                </v-col>
                <v-col cols="12" class="mt-4" v-if="showAddEnhancement">
                  <v-text-field
                    v-model="newEnhancement"
                    density="comfortable"
                    variant="outlined"
                    hide-details
                    label="New Enhancement"
                  >
                    <template #append-inner>
                      <v-icon class="cursor-pointer" color="primary" @click="addEnhancement">
                        mdi-check
                      </v-icon>
                      <v-icon class="cursor-pointer" color="error" @click="clearEnhancement">
                        mdi-close
                      </v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-row>
          </v-container>
          <v-row
            v-if="editorStore.isAutoFix && editorStore.autofixBackgroundType === 'realistic'"
            class="py-0 my-0"
          >
            <v-col cols="12" class="d-flex justify-start my-0 py-0">
              <p class="text-subtitle-1 font-weight-bold mt-2">Overlay</p>
            </v-col>
            <v-col
              v-for="(item, index) in autofixOverlays"
              :key="index"
              cols="auto"
              class="pr-0 py-0 my-1"
            >
              <v-card
                variant="outlined"
                :model-value="4"
                :class="{
                  'border-md border-opacity-100 border-primary': selectedOverlay === index,
                }"
                @click.stop="selectNewOverlay(index)"
                :disabled="canvasStore.canvasLoader"
              >
                <v-row no-gutters>
                  <v-col class="ma-1">
                    <v-card>
                      <div style="width: 75px; height: 45px; background-color: white">
                        <img
                          :src="item !== null ? autofixOverlays[index] : undefined"
                          width="75"
                          height="45"
                          contain
                          crossorigin="anonymous"
                          :style="{ filter: 'invert(1)' }"
                          @error="onImageError"
                          @load="onImageLoad"
                        />
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <!-- Advanced Settings -->

          <div
            class="text-subtitle-2 text-medium-emphasis text-start pt-4 font-weight-bold"
            v-if="enableAdvancedSettings && !editorStore.isAutoFix"
          >
            <span class="text-black">
              {{ t('labels.advancedSettings') }}
            </span>
          </div>

          <AdvancedSettings
            v-if="enableAdvancedSettings && !editorStore.isAutoFix"
            :is-shuffling-placement="isShufflingPlacement"
            @changeRandomShadow="changeRandomShadow"
          ></AdvancedSettings>

          <v-row v-if="editorStore.isAutoFix" class="my-0 py-0" no-gutters>
            <v-col cols="12" class="d-flex justify-start my-0 py-0">
              <p class="text-subtitle-1 font-weight-bold mt-2">Text Color</p>
            </v-col>
            <v-col class="py-0 my-1 pr-0" cols="auto">
              <v-menu :close-on-content-click="false" location="top" v-model="textMenu">
                <template v-slot:activator="{ props }">
                  <v-btn
                    size="40"
                    class="mx-1 cursor-pointer"
                    style="border: 1px solid black"
                    icon="mdi-plus"
                    v-bind="props"
                  >
                  </v-btn>
                </template>
                <v-card width="350" elevation="4">
                  <p class="text-subtitle-1 font-weight-bold ml-2 mt-2">Text</p>
                  <v-row no-gutters class="align-center">
                    <v-col cols="auto">
                      <v-color-picker
                        elevation="0"
                        class="color-picker__class"
                        v-model="newTextColor"
                        mode="hex"
                        :modes="['hex']"
                        hide-inputs
                        hide-canvas
                      ></v-color-picker>
                    </v-col>
                    <v-col cols="auto">
                      <v-select
                        v-model="selectedTextColorCode"
                        variant="solo-filled"
                        density="compact"
                        flat
                        item-value="id"
                        item-title="code"
                        hide-details
                        :items="colorCodeTypes"
                      ></v-select>
                    </v-col>
                    <v-col class="ml-4" cols="4">
                      <v-text-field
                        v-model="textColor"
                        variant="solo-filled"
                        flat
                        density="compact"
                        hide-details
                        :prefix="selectedTextColorCode === colorCodes.HEX ? '#' : undefined"
                      ></v-text-field>
                    </v-col>
                    <v-col class="ml-4">
                      <v-menu :close-on-content-click="false" location="end" offset="8">
                        <!-- Activator -->
                        <template #activator="{ props }">
                          <v-btn v-bind="props" :color="newTextColor" rounded="1" size="36">
                          </v-btn>
                        </template>

                        <!-- Menu Content -->
                        <v-card>
                          <v-color-picker
                            class="color-picker__menu"
                            variant="outlined"
                            v-model="newTextColor"
                            mode="rgba"
                            hide-inputs
                            flat
                          ></v-color-picker>
                        </v-card>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-card-actions class="d-flex justify-end card_action__class mb-0 pb-0">
                    <v-btn variant="text" color="grey" @click.stop="closeTextMenu">Close</v-btn>
                    <v-btn variant="tonal" color="primary" @click.stop="addNewTextColor">Add</v-btn>
                  </v-card-actions>
                  <p v-show="alreadyPresent" class="ml-2 mb-1 text-red">
                    Text Color already present
                  </p>
                </v-card>
              </v-menu>
            </v-col>
            <v-col
              cols="auto"
              v-for="(item, index) in autofixStore.autofixColors"
              :key="index"
              class="py-0 my-1 pr-1 pl-2"
            >
              <v-btn
                :color="getOutlineColor(item)"
                size="40"
                class="text-capitalize font-weight-bold"
                flat
                :class="{
                  'border-lg border-opacity-100 border-primary':
                    canvasStore.textColor.toLowerCase() === item.toLowerCase(),
                }"
                @click="selectNewTextColor(index)"
                elevation="2"
                ><span :style="{ color: item, fontSize: '20px' }">Aa</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>

      <!-- Canvas -->
      <v-col cols="6">
        <v-row class="px-5 mx-1 d-flex align-center justify-space-between">
          <div class="d-flex gap-2">
            <v-btn
              variant="outlined"
              rounded
              color="primary"
              class="text-capitalize"
              append-icon="mdi-creation"
              :disabled="isShufflingPlacement || canvasStore.canvasLoader"
              @click="changeRandomShadow"
              v-if="!editorStore.isAutoFix"
            >
              {{ t('labels.shuffle') }}
            </v-btn>
          </div>

          <div class="d-flex gap-2">
            <v-tooltip bottom>
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="outlined"
                  rounded
                  icon="mdi-undo"
                  :disabled="
                    !undoRedoStore.canUndo ||
                    isShufflingPlacement ||
                    canvasStore.canvasLoader ||
                    editorStore.isShuffleLoading ||
                    undoRedoStore.isUndoRedoInProgress
                  "
                  @click="undoRedoStore.undo()"
                  size="small"
                >
                </v-btn>
              </template>
              <span>Undo (Ctrl+Z)</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  variant="outlined"
                  rounded
                  icon="mdi-redo"
                  :disabled="
                    !undoRedoStore.canRedo ||
                    isShufflingPlacement ||
                    canvasStore.canvasLoader ||
                    editorStore.isShuffleLoading
                  "
                  @click="undoRedoStore.redo()"
                  size="small"
                >
                </v-btn>
              </template>
              <span>Redo (Ctrl+Y)</span>
            </v-tooltip>
          </div>
        </v-row>
        <v-row class="mt-5">
          <v-col class="canvas-wrapper d-flex align-center justify-center h-100 w-100">
            <v-progress-circular
              v-show="
                (canvasStore.canvasLoader && !canvasStore.hideLoader && !canvasStore.canvasError) ||
                isShufflingPlacement ||
                editorStore.isShuffleLoading
              "
              class="position-absolute top-50 left-50"
              size="48"
              color="primary"
              indeterminate
            ></v-progress-circular>
            <canvas
              v-show="
                !canvasStore.canvasError &&
                !canvasStore.canvasLoader &&
                !isShufflingPlacement &&
                !editorStore.isShuffleLoading &&
                !editorStore.isRegenerating
              "
              ref="canvasRef"
            >
            </canvas>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
// ============================================================================
// IMPORTS
// ============================================================================

// Vue imports
import {
  ref,
  computed,
  watch,
  onMounted,
  inject,
  onBeforeMount,
  onUnmounted,
  reactive,
  nextTick,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

// Component imports
import MenuOption from '@/components/MenuOption.vue'
import type { VTextField } from 'vuetify/components'
import SlideGroupSelector from '@/components/SlideGroupSelector.vue'
import AdvancedSettings from '@/components/AdvancedSettings.vue'
import ProductEditor from '@/components/ProductEditor.vue'

// Store imports
import { useCanvasStore } from '@/stores/canvas'
import { useEnhancementsStore } from '@/stores/enhancement'
import { useUndoRedoStore } from '@/stores/undoRedo'
import { useProductStore } from '@/stores/product'
import { useBrandStore } from '@/stores/brand'
import { usePlacementStore } from '@/stores/placement'
import { useAssetStore } from '@/stores/asset'
import { useAppStore } from '@/stores/app'
import { useEditorStore } from '@/stores/editor'
import { useCreativeStore } from '@/stores/creative'

// Service imports
import CreativeService from '@/services/creativeService'
import UndoRedoService from '@/services/undoRedoService'
import EditorService from '@/services/editorService'

// Helper imports
import { debounce, isEqual, over } from 'lodash'
import {
  getContrastColor,
  uploadFabricCanvasToS3,
  logCreative,
  moveToBottom,
  moveToFront,
  renderChevronIcon,
  getRandomNumber,
  getErrorMessage,
} from '@/helpers/commonHelper'
import { type ProductData } from '@/helpers/productPlacementHelper'
import { getDominantColor, loadHtmlImage, showGlowLayer } from '@/helpers/imageHelper'
import {
  reflowTextAfterScaling,
  reflowTextBasedOnCollisions,
  handleUSToUKEnglish,
  USkeywords,
  applyAutofitToTextObject,
  applyMaxLinesAndVerticalOffset,
} from '@/helpers/textHelper'
import { syncContactShadow } from '@/helpers/shadowHelper'
import {
  getProminentColors,
  hslToRgb,
  parseRgbString,
  rgbToHex,
  rgbToHsl,
} from '@/helpers/colorHelper'
import {
  overlays,
  overlay6Thumbnail,
  specialBackgrounds,
  autofixOverlays,
} from '@/helpers/backgroundHelper'
import { addDuoToneBackground, removeDuoToneBackground } from '@/helpers/canvasHelper'

// Fabric.js imports
import { Canvas, Control, FabricObject, Group, loadSVGFromURL, Rect, util } from 'fabric'
import { FabricImage } from 'fabric'

// Type imports
import type { EnhancingItem } from '@/types/enhancingItem'
import type { Logo } from '@/types/logo'
import type { FooterConfigProvider } from '@/types/footerConfig'
import type { ProductWithInfo } from '@/types/product'
import { TEXT_TYPE_NAMES, type TextTypeName } from '@/types/textTypeName'

// Constant imports
import { discountTypes, discountVariations } from '@/constants/canvas'
import { PLACEMENT_TYPES, PRODUCT_ASPECT_TYPES, SPECIAL_PLACEMENTS } from '@/constants/positions'
import { embedMessageTypes } from '@/constants/embed'

// Event bus
import emitter from '@/eventBus'
import { layerNames } from '@/constants/layers'
import type { GenerateBgWithOverlayRequest } from '@/types/generateBgWithOverlayRequest'
import { argbFromHex, TonalPalette, hexFromArgb } from '@material/material-color-utilities'
import { useAutofixStore } from '@/stores/autofix'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'

// ============================================================================
// STORE INSTANCES
// ============================================================================

const canvasStore = useCanvasStore()
const enhancementsStore = useEnhancementsStore()
const creativeStore = useCreativeStore()
const undoRedoStore = useUndoRedoStore()
const productStore = useProductStore()
const brandStore = useBrandStore()
const placementStore = usePlacementStore()
const appStore = useAppStore()
const assetStore = useAssetStore()
const editorStore = useEditorStore()
const autofixStore = useAutofixStore()
const adSizeConfigStore = useAdSizeConfigStore()

// ============================================================================
// SERVICE INSTANCES
// ============================================================================

const creativeService = new CreativeService()
const undoRedoService = new UndoRedoService()
const editorService = new EditorService()

// ============================================================================
// INJECTIONS & COMPOSABLES
// ============================================================================

const { footerConfig, setFooterConfig } = inject<FooterConfigProvider>('footerConfig')!
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const notify = inject('notify') as Function

// ============================================================================
// REACTIVE STATE
// ============================================================================

// Canvas related
const canvasRef = ref<HTMLCanvasElement | null>(null)
let fabricCanvas: any = canvasStore.fabricCanvas
const canvasZoom = ref<number>(0)

// Form field refs
const headlineField = ref<VTextField>()
const subheadlineField = ref<VTextField>()
const discountValueField = ref<VTextField>()
const discountValueMenuField = ref<VTextField>()

// Menu states
const colorMenu = ref(false)
const editMenu = ref(false)
const colorCodes = {
  HEX: 'Hex',
  RGB: 'RGB',
}
const textMenu = ref(false)
const colorCodeTypes = [colorCodes.HEX, colorCodes.RGB]
const newTextColor = ref<string>('#ffffff')
const selectedTextColorCode = ref(colorCodes.HEX)
const editMenuBackground = ref(false)
const discountMenu = ref(false)
// These refs are now managed by the editor store
const headlinePromptMenu = computed({
  get: () => editorStore.headlinePromptMenu,
  set: (value: boolean) => editorStore.setHeadlinePromptMenu(value),
})

const subheadlinePromptMenu = computed({
  get: () => editorStore.subheadlinePromptMenu,
  set: (value: boolean) => editorStore.setSubheadlinePromptMenu(value),
})
const bgTexturePromptMenu = ref(false)

// UI states
const showSubheadline = ref(true)
const showAddEnhancement = ref(false)
const showPlaceholder = ref(false)
// These refs are now managed by the editor store
const enableAdvancedSettings = computed({
  get: () => editorStore.enableAdvancedSettings,
  set: (value: boolean) => editorStore.setEnableAdvancedSettings(value),
})

const isShufflingPlacement = computed({
  get: () => editorStore.isShufflingPlacement,
  set: (value: boolean) => editorStore.setIsShufflingPlacement(value),
})

const isRegenerating = ref<boolean>(false)
const isNextLoading = ref<boolean>(false)
const resetLoader = ref<boolean>(false)

// Validation states
const isHeadlineValid = ref<boolean>(true)
const isSubheadlineValid = ref<boolean>(true)
const isDiscountFieldValid = ref<boolean>(true)

// Loading states
const enhanceLoading = ref<boolean>(false)
// These refs are now managed by the editor store
const writingHeadlineUsingAI = computed({
  get: () => editorStore.writingHeadlineUsingAI,
  set: (value: boolean) => {
    editorStore.setWritingHeadlineUsingAI(value)
  },
})

const writingSubheadlineUsingAI = computed({
  get: () => editorStore.writingSubheadlineUsingAI,
  set: (value: boolean) => {
    subHeadlinePrompt.value = null
    editorStore.setWritingSubheadlineUsingAI(value)
  },
})
const isLoadingHeadlinesUsingDiscount = ref(false)

// Logo related
const logoDimensionsStored = ref<boolean>(false)
const logoWidth = ref<number>(100)
const logoHeight = ref<number>(100)

// Font sizes
const headlineFontSize = ref<number>(72)
const subheadlineFontSize = ref<number>(48)

// Form data
const newEnhancement = ref('')
const headlinePrompt = ref()
const subHeadlinePrompt = ref()
// Form refs
const headlinePromptRef = ref()
const subHeadlinePromptRef = ref()
const bgTexturePromptRef = ref()

const disableResetCounter = ref(0)

// Tooltip
const tooltip = document.createElement('div')

const alreadyPresent = ref<boolean>(false)

// Arrays
let lastRandomIndex: number[] = []

// ============================================================================
// CONSTANTS
// ============================================================================

const discountTypesInfo = {
  percentage: {
    label: 'Percentage',
    value: discountTypes.percentage,
  },
  fixedAmount: {
    label: 'Fixed Amount',
    value: discountTypes.fixed,
  },
  noDiscount: {
    label: 'No Discount',
    value: discountTypes.none,
  },
}

const discountVariationsInfo = {
  upto: {
    label: 'UP TO',
    value: discountVariations.upto,
  },
  flat: {
    label: 'FLAT',
    value: discountVariations.flat,
  },
}

const specialPlacements = [PLACEMENT_TYPES.FRONT, PLACEMENT_TYPES.STAIRCASE, PLACEMENT_TYPES.LAYER]
const specialTexturesIndex = [1, 2]
// ============================================================================
// REACTIVE OBJECTS
// ============================================================================

const tempDiscountDetails = ref<{
  type: string
  value: string
  couponCode: string
  variation: string
}>({
  type: discountTypes.none,
  value: '',
  couponCode: '',
  variation: discountVariations.upto,
})

// ============================================================================
// COMPUTED PROPERTIES
// ============================================================================

// Selected indices
const selectedColor = computed(() => brandStore.selectedColor)
const selectedOverlay = computed(() =>
  autofixOverlays.findIndex((overlay) => overlay === placementStore.selectedOverlayImage),
)
const selectedTextureIndex = computed(() => {
  if (placementStore.selectedBackgroundImage === '') {
    return null
  }
  return backgroundImages.value.findIndex(
    (image) => image === placementStore.selectedBackgroundImage,
  )
})

const selectedImageIndex = computed(() => {
  if (placementStore.selectedAIBackgroundImage === '') {
    return null
  }
  return aiBackgroundImages.value.findIndex(
    (image) => image === placementStore.selectedAIBackgroundImage,
  )
})

const selectedOverlayIndex = computed(() => {
  if (placementStore.selectedOverlayImage === '') {
    return null
  }
  return overlays.findIndex((overlay) => overlay === placementStore.selectedOverlayImage)
})

// Background images
const backgroundImages = computed(() => {
  return placementStore.backgroundImages || []
})

const aiBackgroundImages = computed(() => {
  return placementStore.aiBackgroundImages
})

// Canvas related
const baseGeneratedCanvasJson = computed(() => canvasStore.baseGeneratedCanvasJson)

// Text length limits from config
const headlineUILimits = computed(() => adSizeConfigStore.headlineUILimits)
const maxLength = computed(() => headlineUILimits.value.hardLimit)
const recommendedLength = computed(() => headlineUILimits.value.recommendedLimit)
const warningThreshold = computed(() => headlineUILimits.value.warningThreshold)
const maxSubheadlineLength = computed(() => adSizeConfigStore.subheadlineHardLimit)

const recommendedSubheadlineLength = computed(() => {
  return 40
})

const warningThresholdForSubheadline = computed(() => {
  return 41
})

// Validation rules
const headlineRules = computed(() => [
  (v: string) => {
    const length = v?.length || 0
    if (length > maxLength.value) {
      isHeadlineValid.value = false
      return 'Character limit exceeded. Please shorten your text.'
    }
    if (length >= warningThreshold.value) {
      isHeadlineValid.value = true
      return `${recommendedLength.value} characters recommended.`
    }
    isHeadlineValid.value = true
    return true
  },
])

const discountValueRules = computed(() => [
  (v: number) => {
    if (!v) {
      isDiscountFieldValid.value = false
    } else if (v <= 0) {
      isDiscountFieldValid.value = false
      return 'Value must be greater than 0'
    } else if (
      discountMenu.value &&
      tempDiscountDetails.value.type === discountTypes.percentage &&
      v > 90
    ) {
      isDiscountFieldValid.value = false
      return 'Valid Range 0-90'
    } else if (
      !discountMenu.value &&
      selectedDiscountType.value === discountTypes.percentage &&
      v > 90
    ) {
      isDiscountFieldValid.value = false
      return 'Valid Range 0-90'
    } else {
      isDiscountFieldValid.value = true
    }
    return true
  },
])

const subheadlineRules = computed(() => [
  (v: string) => {
    const length = v?.length || 0
    if (length > maxSubheadlineLength.value) {
      isSubheadlineValid.value = false
      return 'Character limit exceeded. Please shorten your text.'
    }
    if (length >= warningThresholdForSubheadline.value) {
      isSubheadlineValid.value = true
      return `${recommendedSubheadlineLength.value} characters recommended.`
    }
    isSubheadlineValid.value = true
    return true
  },
])

// Warning states
const isHeadlineWarning = computed(() => {
  const result = headlineRules.value[0](editorStore.headline)
  return typeof result === 'string' && result.includes('recommended')
})

const isSubheadlineWarning = computed(() => {
  const result = subheadlineRules.value[0](editorStore.subheadline)
  return typeof result === 'string' && result.includes('recommended')
})

// UI visibility
const showEnhancingOptions = computed(() => {
  return !isEqual(placementStore.selectedPublishedVariant?.canvasJson, canvasStore.canvasJson)
})

// Enhancement items
const enhancingItems = computed(() => {
  return enhancementsStore.enhancingItems
})

const selectedBackdrop = computed({
  get: () => editorStore.selectedBackdrop,
  set: (value: string) => {
    editorStore.selectedBackdrop = value
  },
})

const selectedBackground = computed({
  get: () => editorStore.selectedBackground,
  set: (value: string) => {
    editorStore.selectedBackground = value
  },
})

const selectedItem = computed({
  get: () => enhancementsStore.selectedItem,
  set: (value: number | null) => {
    enhancementsStore.updateSelectedItem(value)
  },
})

// Discount related
const discountSummary = computed(() => {
  if (canvasStore.lastUsedDiscountInfo.type === discountTypes.none) {
    return 'No Discount Applied'
  } else {
    const discountInfo = canvasStore.lastUsedDiscountInfo
    const symbol =
      discountInfo.type === discountTypes.percentage ? '%' : appStore.profileCurrencySymbol
    let mssgWithoutDiscountCode = ''
    if (discountInfo.type === discountTypes.percentage) {
      mssgWithoutDiscountCode = `${discountInfo.value}${symbol} discount applied`
    } else {
      mssgWithoutDiscountCode = `${symbol}${discountInfo.value} discount applied`
    }
    if (discountInfo.couponCode) {
      return `${mssgWithoutDiscountCode}, with code ${discountInfo.couponCode.toUpperCase()}`
    }
    return mssgWithoutDiscountCode
  }
})

const upperCaseCouponCode = computed({
  get: () => canvasStore.rawCouponCode.toUpperCase(),
  set: (value: string) => {
    canvasStore.setRawCouponCode(value.toUpperCase())
  },
})

const tempDiscountCouponCode = computed({
  get: () => tempDiscountDetails.value.couponCode.toUpperCase(),
  set: (value: string) => {
    tempDiscountDetails.value.couponCode = value.toUpperCase()
  },
})

const isDiscountEnabled = computed(() => {
  return canvasStore.discountType != discountTypes.none
})

const selectedDiscountType = computed({
  get: () => canvasStore.discountType,
  set: (value) => {
    if (value == discountTypes.none) {
      canvasStore.setDiscountVariation(discountVariations.upto)
      canvasStore.setDiscountValue('')
      canvasStore.setRawCouponCode('')
    }
    canvasStore.setDiscountType(value)
  },
})

// Logo selection
const selectedLogoIndex = computed({
  get: () => brandStore.logos.findIndex((logo: Logo) => logo === brandStore.selectedLogo),
  set: async (newIndex) => {
    if (newIndex >= 0 && newIndex < brandStore.logos.length) {
      const logo = brandStore.logos[newIndex]
      await updateCanvasJsonLogo(logo)
    }
  },
})

// Background related
const backDrops = computed(() => {
  return editorStore.createdBackDrops
})

const hiddenBackDropIndexes = computed(() => {
  if (specialPlacements.includes(placementStore.selectedPlacementOption)) {
    return []
  } else {
    const specialBackdrops: Array<number> = []
    for (let i = 0; i < backDrops.value.length; i++) {
      const backdrop = backDrops.value[i]
      if (
        specialTexturesIndex.includes(backdrop.backgroundTextureIndex) &&
        !editorStore.specialIndex.find((item) => item.index === i)
      ) {
        specialBackdrops.push(i)
      }
    }
    return specialBackdrops
  }
})

const allBackGrounds = computed(() => {
  return editorStore.createdBackGrounds
})

const isToggleDisabled = computed(() => {
  return isShufflingPlacement.value || canvasStore.canvasLoader
})

// Filtered Placement Options
const filteredPlacementOptions = computed(() => {
  if (editorStore.selectedBackgroundOption == 1) {
    return placementStore.placementOptions
  }
  const products = editorStore.activeProductDataItems
  const allHorizontal =
    products.length >= 2 &&
    products.every((p) => p.productData?.aspectType === PRODUCT_ASPECT_TYPES.HORIZONTAL)

  return placementStore.placementOptions.filter(
    (option) =>
      SPECIAL_PLACEMENTS.includes(option) || (allHorizontal && option === PLACEMENT_TYPES.CORNER),
  )
})

// Enhancement prompt
const enhancementPrompt = computed(() => {
  return {
    positive_prompt: `On a canvas with background color ${brandStore.selectedColor}, design a minimal arrangement strictly on the right side using ${selectedItem.value !== null ? enhancingItems.value[selectedItem.value]?.name : 'default'}. Keep the effects minimal. Strictly place the elements on the right side and leave the left side completely empty.`,
    negative_prompt:
      'poorly drawn, bad anatomy, bad hands, bad eyes, missing fingers, extra fingers, ugly, deformed, disfigured, blurry, grainy, out of focus, low resolution, amateur, poorly lit, oversaturated, undersaturated, watermark, signature, text, writing, noise, artifacts',
    user_prompt: selectedItem.value !== null ? enhancingItems.value[selectedItem.value]?.name : '',
    bpid: `${appStore.selectedBusinessProfileId}`,
  }
})

const disableReset = computed(() => {
  const base = JSON.stringify(baseGeneratedCanvasJson.value)
  const current = editorService.getCanvasJsonWithCustomInfo(canvasStore.fabricCanvas)

  // Remove objects with layerName 'BackgroundShape' from both base and current before comparing
  const baseObj = base ? JSON.parse(base) : null
  const currentObj = current ? JSON.parse(JSON.stringify(current)) : null
  if (baseObj && typeof baseObj === 'object' && baseObj.objects) {
    baseObj.objects = baseObj.objects.filter(
      (obj: any) =>
        obj.layerName !== 'BackgroundShape' &&
        obj.layerName !== 'BG' &&
        obj.layerName != 'Shadow' &&
        obj.layerName != 'BackgroundTexture',
    )
  }
  if (currentObj && typeof currentObj === 'object' && currentObj.objects) {
    currentObj.objects = currentObj.objects.filter(
      (obj: any) =>
        obj.layerName !== 'BackgroundShape' &&
        obj.layerName !== 'BG' &&
        obj.layerName != 'Shadow' &&
        obj.layerName != 'BackgroundTexture',
    )
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  disableResetCounter.value
  return (
    !current ||
    JSON.stringify(baseObj) == JSON.stringify(currentObj) ||
    resetLoader.value ||
    canvasStore.canvasLoader ||
    !undoRedoStore.canUndo
  )
})

// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================

onBeforeMount(async () => {
  if (!editorStore.isAutoFix) {
    await editorService.generateBackdropAndBackground()
  }
})

onMounted(async () => {
  window.onbeforeunload = () => {
    if (route.name === 'Editor') return ''
  }
  const canvasJson = canvasStore.canvasJson
  if (canvasRef.value) {
    const adSize = placementStore.selectedPlacement?.creativeSetGuidelines.ad_size
    const defaultSize = adSizeConfigStore.canvasSize
    fabricCanvas = new Canvas(canvasRef.value, {
      width: adSize?.width || defaultSize.width,
      height: adSize?.height || defaultSize.height,
      preserveObjectStacking: true,
      selection: false,
    })
    fabricCanvas.on('object:modified', function (e: any) {
      const product = e.target
      const layerName = (product as any)?.layerName || ''
      const isTargetProduct = layerName.includes('Product') || layerName.includes('glowGroup')
      if (!isTargetProduct) return

      const objects = fabricCanvas.getObjects()
      const headline = objects.find((obj: any) => obj.layerName.includes('Headline'))
      headline.previousHeight = headline.height

      product.setCoords()

      if (layerName.includes('glowGroup')) {
        reflowTextAfterScaling(canvasStore.fabricCanvas, product)
      } else {
        const products = objects.filter((obj: any) => obj.layerName.includes('Product'))
        const sortedProducts = [...products].sort((a, b) => {
          const aLeft = a.getBoundingRect().left
          const bLeft = b.getBoundingRect().left
          return aLeft - bLeft
        })
        reflowTextBasedOnCollisions(canvasStore.fabricCanvas, sortedProducts)
      }
      disableResetCounter.value++
    })
    fabricCanvas.on('object:scaling', function (e: any) {
      disableResetCounter.value++
    })
    fabricCanvas.on('object:moving', function (e: any) {
      disableResetCounter.value++
    })
    fabricCanvas.on('object:rotating', function (e: any) {
      disableResetCounter.value++
    })
    fabricCanvas.on('object:skewing', function (e: any) {
      disableResetCounter.value++
    })

    updateCanvasZoom()
    fabricCanvas.backgroundColor = 'lightgray'
    if (canvasJson) {
      canvasStore.setCanvasLoader(true)
      canvasStore.setHideLoader(false)
      await loadCanvasData()
      loadNewCanvasData()
    }

    canvasStore.setFabricCanvas(fabricCanvas)

    // Set up undo/redo transformation tracking
    undoRedoService.setupCanvasTransformationTracking()

    // Set up keyboard shortcuts for undo/redo
    const handleKeydown = (event: KeyboardEvent) => {
      // Handle Ctrl+Z (undo) and Ctrl+Y (redo)
      const isUndoKey = (event.ctrlKey || event.metaKey) && !event.shiftKey && event.key === 'z'
      const isRedoKey = (event.ctrlKey || event.metaKey) && event.key === 'y'

      if (isUndoKey || isRedoKey) {
        event.preventDefault()
        event.stopPropagation()
        // Fire and forget - the async operation will complete on its own
        undoRedoStore.handleKeyboardShortcut(event)
      }
    }
    document.addEventListener('keydown', handleKeydown)

    // Store the handler for cleanup
    ;(undoRedoService as any).keydownHandler = handleKeydown

    const wrapper = document.querySelector('.canvas-wrapper')
    if (wrapper) {
      const resizeObserver = new ResizeObserver(() => {
        updateCanvasZoom()
      })
      resizeObserver.observe(wrapper)
    }
  }
  // if (canvasStore.counter == 0) {
  //   const randomIndex = Math.floor(Math.random() * (placementStore.backgroundImages.length - 2)) + 2
  //   placementStore.setSelectedBackgroundImage(placementStore.backgroundImages[randomIndex])
  // }
  if (editorStore.isAutoFix) {
    canvasStore.setCanvasLoader(true)
    canvasStore.setHideLoader(false)
    editorService.triggerTextReflow()
    const bgUrl = placementStore.selectedBackgroundImage
    let url = bgUrl
    if (editorStore.autofixBackgroundType === 'realistic') {
      const index = editorStore.contrastRatio < 6 ? 1 : 0
      const overlayUrl = autofixOverlays[index]
      placementStore.selectedOverlayImage = overlayUrl
      if (index === 1) {
        const backgroundColor = await getOverlayColorForAutoFix(url)
        brandStore.selectedColor = backgroundColor
        url = await creativeService.GenerateBackgroundWithOverlay(
          appStore.selectedBusinessProfileId,
          {
            brandName: brandStore.selectedBrand?.name || '',
            bgUrl,
            overlayUrl,
            color: backgroundColor,
          } as GenerateBgWithOverlayRequest,
        )
      }
    }
    await editorService.addBackgroundImage(canvasStore.fabricCanvas, url)
    const logoUrl = brandStore.selectedLogo?.logoUrl || brandStore.logos[0]
    const newIndex = brandStore.logos.findIndex((logo: Logo) => logo.logoUrl === logoUrl)
    const newLogo = brandStore.logos[newIndex]
    brandStore.baseLogo = newLogo
    await editorService.updateLogo(newLogo)
    extendToObject()
    const json = editorService.getCanvasJsonWithCustomInfo(fabricCanvas)
    canvasStore.setCanvasJson(json)
    canvasStore.setEnableResetCanvas(true, json, placementStore.selectedBackgroundImage)
    canvasStore.setCanvasLoader(false)
    canvasStore.setHideLoader(true)
  }

  // Set disount code values
  tempDiscountDetails.value = {
    type: canvasStore.discountType,
    value: canvasStore.discountValue,
    couponCode: canvasStore.rawCouponCode,
    variation: canvasStore.discountVariation,
  }
  setFooterConfig({
    showNext: true,
    showBack: true,
    disableBack: false,
    showReset: true,
    disableNext: false,
    loadingNext: isNextLoading.value,
    disableReset: disableReset.value,
    onNext: handleNext,
    onReset: handleReset,
  })
})

const extendToObject = () => {
  FabricObject.prototype.toObject = (function (toObject) {
    return function (this: any) {
      return {
        ...toObject.call(this),
        productId: this.productId,
        layerId: this.layerId,
        layerName: this.layerName,
        layerType: this.layerType,
        productsCount: this.productsCount,
        autofit: this.autofit,
        maxLines: this.maxLines,
        text: this.text,
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        fontFamily: this.fontFamily,
        fontStyle: this.fontStyle,
        lineHeight: this.lineHeight,
        editable: this.editable,
        textAlign: this.textAlign,
        imageFit: this.imageFit,
        positionIndex: this.positionIndex,
        hasOriginChanged: this.hasOriginChanged,
        deltaY: this.deltaY,
        renderAtLevel: this.renderAtLevel,
        aiShadowProperties: this.aiShadowProperties,
        rx: this.rx,
        ry: this.ry,
      }
    }
  })(FabricObject.prototype.toObject)
}

onBeforeRouteLeave((to, from, next) => {
  if (to.name === 'Generate' && from.name === 'Editor') {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?')
    if (answer) {
      // Reset all Locks
      editorStore.resetAllLocks()
      canvasStore.resetCanvasState()
      editorStore.resetEditorStore()
      const aiTextureUrl = overlays[0]
      placementStore.setOverlayImage(aiTextureUrl)
      placementStore.resetSelectedPlacementOption('')
      brandStore.selectedBackgroundColorType = 0
      brandStore.selectedTextColorType = 0
      // Also clear any undo/redo history when leaving the editor
      undoRedoStore.clearHistory()
      next()
    } else {
      next(false)
    }
  } else if (to.name === 'Mode' && from.name === 'Editor') {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?')
    if (answer) {
      autofixStore.resetAutofixState()
      editorStore.isAutoFix = false
      next()
    } else {
      next(false)
    }
  } else if (to.name === 'Validate' && from.name === 'Editor') {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?')
    if (answer) {
      autofixStore.resetAutofixState()
      editorStore.isAutoFix = false
      next()
    } else {
      next(false)
    }
  } else {
    next()
  }
})

const getOverlayColorForAutoFix = async (bgUrl: string) => {
  const prominentColors = await getProminentColors(bgUrl)
  //choose random prominent color
  const randomIndex = getRandomNumber(0, 0, prominentColors.length - 1)
  const prominentColor = prominentColors[randomIndex]
  const rgbColor = parseRgbString(prominentColor)
  if (!rgbColor) {
    throw new Error('Invalid RGB color format')
  }
  const hsl = rgbToHsl(rgbColor)
  // Set luminosity to 0.8 (80%)
  const adjustedRgb = hslToRgb(hsl[0], hsl[1], 0.8)
  const hexColor = rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b)
  const argb = argbFromHex(hexColor)
  const palette = TonalPalette.fromInt(argb)
  const backgroundColor = hexFromArgb(palette.tone(90))
  return backgroundColor
}

onUnmounted(() => {
  // Clean up undo/redo transformation tracking
  undoRedoService.destroy()

  // Remove keyboard event listener
  const keydownHandler = (undoRedoService as any).keydownHandler
  if (keydownHandler) {
    document.removeEventListener('keydown', keydownHandler)
  }
  setFooterConfig({
    showNext: false,
    showBack: false,
    showReset: false,
    disableNext: false,
    loadingNext: false,
    disableReset: false,
    onNext: () => {},
  })
  window.onbeforeunload = null
})

// ============================================================================
// WATCHERS
// ============================================================================

watch(isNextLoading, (newValue) => {
  footerConfig.loadingNext = newValue
})

watch(disableReset, (newValue) => {
  footerConfig.disableReset = newValue
})

watch(
  () => selectedItem.value,
  async (newValue, oldValue) => {
    if (newValue !== null) {
      const name = enhancingItems.value[newValue]?.name
      const item = enhancingItems.value.find((item: EnhancingItem) => item.name == name)
      const key = `${selectedColor.value}`

      if (item?.apply[key]) {
        canvasStore.setBgImageUrl(item.apply[key])
        await editorService.addBackgroundImage(fabricCanvas, item.apply[key])
      } else {
        await enhanceCreative(false, oldValue)
      }
    } else if (oldValue !== null && newValue === null) {
      await selectNewBackDrop(oldValue, editorStore.selectedBackDropIndex || 0)
      canvasStore.setBgImageUrl('')
    }
  },
)

watch(
  () => editorStore.headline,
  (newValue, oldValue) => {
    if (newValue && USkeywords.some((word) => newValue.toLowerCase().includes(word))) {
      editorStore.setHeadline(handleUSToUKEnglish(newValue))
    }
    setTimeout(() => headlineField.value?.validate(), 0)
  },
)

watch(
  () => editorStore.subheadline,
  (newValue, oldValue) => {
    if (newValue && USkeywords.some((word) => newValue.toLowerCase().includes(word))) {
      editorStore.setSubheadline(handleUSToUKEnglish(newValue))
    }
    setTimeout(() => subheadlineField.value?.validate(), 0)
  },
)

watch(
  () => canvasStore.discountType,
  () => {
    setTimeout(() => discountValueField.value?.validate(), 0)
  },
)

watch(
  () => canvasStore.lastUsedDiscountInfo,
  () => {
    tempDiscountDetails.value = {
      type: canvasStore.discountType,
      value: canvasStore.discountValue,
      couponCode: canvasStore.rawCouponCode,
      variation: canvasStore.discountVariation,
    }
  },
)

watch(
  () => tempDiscountDetails.value.type,
  (newValue, oldValue) => {
    canvasStore.setDiscountType(newValue)
    if (newValue != oldValue) {
      tempDiscountDetails.value = {
        type: canvasStore.discountType,
        variation: discountVariations.upto,
        value: '',
        couponCode: '',
      }
    }
    if (newValue === discountTypes.none) {
      isDiscountFieldValid.value = true
    }
    setTimeout(() => discountValueMenuField.value?.validate(), 0)
  },
)

watch(upperCaseCouponCode, () => {
  setTimeout(() => subheadlineField.value?.validate(), 0)
})

watch([isHeadlineValid, isSubheadlineValid], ([headValid, subheadValid]) => {
  canvasStore.setIsTextValidationFailed(!headValid || !subheadValid)
})

watch(headlinePromptMenu, (value) => {
  if (value) {
    setTimeout(() => {
      headlinePromptRef.value?.focus()
    }, 250)
  }
})

watch(subheadlinePromptMenu, (value) => {
  if (value) {
    setTimeout(() => {
      subHeadlinePromptRef.value?.focus()
    }, 250)
  }
})

watch(bgTexturePromptMenu, (value) => {
  if (value) {
    setTimeout(() => {
      bgTexturePromptRef.value?.focus()
    }, 250)
  }
})

watch(
  () => editorStore.selectedBackgroundOption, // getter function
  (value) => {
    if (value == 0) {
      editMenuBackground.value = false
    } else if (value == 1) {
      editMenu.value = false
    }
  },
)

watch(
  () => editorStore.shouldUpdateCanvasData,
  async (value) => {
    if (value) {
      canvasStore.setCanvasLoader(true)
      canvasStore.setHideLoader(false)
      await loadCanvasData(editorStore.updatedCanvasData)
      editorStore.setShouldUpdateCanvasData(false)
    }
  },
)

// ============================================================================
// FUNCTIONS
// ============================================================================

// Menu handlers

const closeMenu = () => {
  editMenu.value = false
}

const closeMainMenu = () => {
  colorMenu.value = false
}

const closeBackgroundMenu = () => {
  editMenuBackground.value = false
}

// Main action handlers
const handleReset = async () => {
  if (!fabricCanvas) return
  fabricCanvas.clear()
  resetLoader.value = true
  canvasStore.setCanvasLoader(true)
  canvasStore.setHideLoader(false)
  const initialCanvasJson = baseGeneratedCanvasJson.value
  canvasStore.setCanvasJson(initialCanvasJson)
  brandStore.setSelectedLogo(brandStore.baseLogo)
  canvasStore.setRawCouponCode(canvasStore.baseDiscountInfo.couponCode)
  canvasStore.setDiscountValue(canvasStore.baseDiscountInfo.value)
  canvasStore.setDiscountType(canvasStore.baseDiscountInfo.type)
  canvasStore.setDiscountVariation(canvasStore.baseDiscountInfo.variation)
  canvasStore.setLastUsedDiscountInfo(canvasStore.baseDiscountInfo)
  editorStore.createdBackDrops = editorStore.baseBackDrops.map((item) => ({ ...item }))
  editorStore.createdBackGrounds = editorStore.baseCreatedBackGrounds.map((item) => ({ ...item }))
  editorStore.setSelectedBackDrop(editorStore.baseSelectedBackDropIndex)
  editorStore.setSelectedBackGround(editorStore.baseSelectedBackGroundIndex)
  editorStore.setSelectedBackgroundOption(editorStore.baseSelectedBackGroundOption)
  editorStore.resetAllLocks()
  if (editorStore.selectedBackgroundOption === 0) {
    editorStore.selectedBackground = '#background0'
    editorStore.selectedBackdrop = ''
  } else {
    editorStore.selectedBackdrop = '#backdrop0'
    editorStore.selectedBackground = ''
  }
  // Reset brand color and recalculate text color based on contrast
  brandStore.selectedBackgroundColorType = 0
  brandStore.selectedTextColorType = 0
  brandStore.selectedColor = brandStore.baseColor
  const backgroundColors = brandStore.colours.map((item) => item) ?? []
  const textColor = getContrastColor(brandStore.selectedColor, backgroundColors)
  canvasStore.setTextColor(textColor)
  placementStore.selectedBackgroundImage = placementStore.baseBackgroundImage
  placementStore.selectedAIBackgroundImage = placementStore.baseAIBackgroundImage
  placementStore.resetSelectedPlacementOption(placementStore.basePlacementType)
  await loadCanvasData(initialCanvasJson)
  tempDiscountDetails.value = {
    type: canvasStore.discountType,
    value: canvasStore.discountValue,
    couponCode: canvasStore.rawCouponCode,
    variation: canvasStore.discountVariation,
  }
  canvasStore.aiGeneratedHeadlines = []
  canvasStore.aiGeneratedSubHeadlines = []
  // Clear undo/redo history after reset so user cannot undo back into pre-reset state
  undoRedoStore.clearHistory()
  undoRedoService.resetObjectStateMapping()
  resetLoader.value = false
}

const handleNext = async () => {
  try {
    isNextLoading.value = true
    const s3Url = await uploadFabricCanvasToS3(fabricCanvas)
    window.top?.postMessage({ type: embedMessageTypes.CREATIVE_URL, value: s3Url }, '*')
    assetStore.setCreativeS3Url(s3Url)
    canvasStore.setCanvasJson(editorService.getCanvasJsonWithCustomInfo(fabricCanvas))
    appStore.incrementStepperCount()
    router.push({ name: 'Download' })
  } catch (error) {
    console.error('Failed to upload image:', error)
    notify('Failed to validate canvas. Please try again.', 'error')
  } finally {
    isNextLoading.value = false
  }
}

// Background and backdrop functions
const addNewBackDrop = async (addedItem: any) => {
  colorMenu.value = false
  if (editorStore.selectedBackgroundOption === 1) {
    selectedBackdrop.value = '#backdrop' + 0
    await undoRedoService.addBackDropsWithUndo(addedItem, 'prepend')
  } else {
    selectedBackground.value = '#background' + 0
    await undoRedoService.addBackGroundsWithUndo(addedItem, 'prepend')
  }
  canvasStore.fabricCanvas.renderAll()
}

const selectNewBackDrop = async (
  oldIndex: number | null,
  newIndex: number,
  cannotPass: boolean = false,
  event?: Event,
) => {
  if (newIndex === editorStore.selectedBackDropIndex && cannotPass) {
    return
  }

  editMenu.value = false
  enhancementsStore.updateSelectedItem(null)

  try {
    // Try to apply new backdrop
    await undoRedoService.selectNewBackDropWithUndo(newIndex)
    canvasStore.fabricCanvas.renderAll()
  } catch (err) {
    console.error('Backdrop change failed, restoring previous backdrop', err)
    notify('Failed to change backdrop, restoring previous backdrop', 'error')
    editorStore.selectedBackDropIndex = oldIndex
  }
}

const editBackDrop = async (newBackdrop: {
  backgroundColor: string
  textColor: string
  backgroundTextureIndex: number
}) => {
  editMenu.value = false
  const index = editorStore.selectedBackDropIndex ?? 0
  await undoRedoService.editBackDropWithUndo(index, newBackdrop)
  canvasStore.fabricCanvas.renderAll()
}

const selectNewBackGround = async (index: number, cannotPass: boolean = false) => {
  if (index === editorStore.selectedBackGroundIndex && cannotPass) {
    return
  }
  editMenuBackground.value = false
  if (filteredPlacementOptions.value.includes(placementStore.selectedPlacementOption)) {
    await undoRedoService.selectNewBackgroundWithUndo(index)
  } else {
    // pick a random placement from all available placement options
    const options = filteredPlacementOptions.value
    if (options.length > 0) {
      const randomIndex = Math.floor(Math.random() * options.length)
      const randomOption = options[randomIndex]
      await undoRedoService.selectPlacementAndBackgroundWithUndo(randomOption, index)
    }
  }
  canvasStore.fabricCanvas.renderAll()
}

const editBackground = async (newbackground: {
  textColor: string
  backgroundImageIndex: number
  backgroundTextureIndex: number | null
}) => {
  editMenuBackground.value = false
  const index = editorStore.selectedBackGroundIndex ?? 0
  await undoRedoService.editBackGroundWithUndo(index, newbackground)
  canvasStore.fabricCanvas.renderAll()
}

// Canvas related functions
const storeLogoDimensions = () => {
  const logoObj = fabricCanvas?.getObjects().find((obj: any) => {
    return (
      obj.visible === true && (obj.layerName === 'Logo' || obj.layerName === 'Logo - Horizontal')
    )
  })
  if (logoObj) {
    logoWidth.value = logoObj.width
    logoHeight.value = logoObj.height
  }
}

const updateCanvasZoom = () => {
  const wrapper = document.querySelector('.canvas-wrapper') as HTMLElement
  const baseWidth = placementStore.selectedPlacement?.creativeSetGuidelines.ad_size.width
  const baseHeight = placementStore.selectedPlacement?.creativeSetGuidelines.ad_size.height
  if (wrapper && baseWidth && baseHeight && fabricCanvas) {
    const maxAvailableWidth = wrapper.clientWidth * 0.9
    const zoom = maxAvailableWidth / baseWidth
    canvasZoom.value = zoom
    fabricCanvas.setZoom(zoom)
    fabricCanvas.setWidth(baseWidth * zoom)
    fabricCanvas.setHeight(baseHeight * zoom)
    fabricCanvas.requestRenderAll()
  }
}

const loadCanvasData = (newCanvasJson: any = null) => {
  const canvasJson = newCanvasJson === null ? canvasStore.canvasJson : newCanvasJson
  return new Promise<void>((resolve) => {
    if (!canvasJson) {
      canvasStore.setCanvasLoader(false)
      resolve()
      return
    }
    fabricCanvas
      .loadFromJSON(canvasJson)
      .then(async () => {
        canvasStore.setCanvasError(false)
        const layers = fabricCanvas.getObjects().reduce((acc: any, obj: any) => {
          acc[obj.layerName] = obj
          return acc
        }, {})
        canvasStore.setLayers(layers)
        const subheadlineObject = fabricCanvas
          .getObjects()
          .find((obj: any) => obj.layerName === 'SubHeadline')
        if (subheadlineObject) {
          subheadlineObject.styles = []
          if (canvasStore.rawCouponCode) {
            const couponCode = canvasStore.rawCouponCode.toUpperCase()
            const simulatedTextLines = subheadlineObject.text?.split('\n')
            const lineIndex = simulatedTextLines?.findIndex((line: any) =>
              line.includes(couponCode),
            )
            if (lineIndex != undefined && lineIndex != -1 && simulatedTextLines) {
              const currentLine = simulatedTextLines[lineIndex]
              const startCharIndex = currentLine.indexOf(couponCode)
              const endCharIndex = startCharIndex + couponCode.length - 1
              for (let i = startCharIndex; i <= endCharIndex; i++) {
                subheadlineObject.styles[lineIndex] = subheadlineObject.styles[lineIndex] || {}
                subheadlineObject.styles[lineIndex][i] = { fontWeight: 'bold' }
              }
            }
          }
        }
        const subheadlineLayer: any = Object.values(layers).find(
          (l: any) => l.layerName === 'SubHeadline',
        )
        Object.values(layers).forEach((layer: any) => {
          if (layer.layerName === 'Headline') {
            /**
             * Adjusts the headline text to allow up to 2 lines before applying autofit.
             *
             * Initially, maxLines is 1, so directly applying autofit shrinks the font size instead of breaking it into multiple lines.
             * This function first sets `maxLines` (up to 2), which allows the text to break naturally.
             * Then, it applies autofit to adjust font size within the allowed lines.
             * If the text expands to two lines, vertical offset is also applied to headline and subheadline
             * to maintain visual alignment after height change.
             */
            applyMaxLinesAndVerticalOffset(layer, subheadlineLayer, layer.previousHeight)
            editorStore.headline = layer.text
          } else if (layer.layerName === 'SubHeadline') {
            applyAutofitToTextObject(layer, layer.fontSize)
            editorStore.subheadline = layer.text
          }
          if (layer.layerName === 'Logo' || layer.layerName === layerNames.AIShadow) {
            layer.set({
              selectable: false,
              evented: false,
            })
          }
          if (layer.layerName === 'Logo - Horizontal') {
            layer.set({
              selectable: false,
              evented: false,
            })
          }
          if (layer.layerName.includes('CTA')) {
            layer.set({
              shadow: {
                color: 'rgba(0,0,0,0.3)',
                blur: 10,
                offsetX: 4,
                offsetY: 4,
              },
            })
          }
        })
        if (!logoDimensionsStored.value) {
          storeLogoDimensions()
          logoDimensionsStored.value = true
        }
        fabricCanvas.getObjects().forEach((obj: any) => {
          if (obj.layerName?.toLowerCase().includes('product')) {
            obj.controls.moveToFrontControl = new Control({
              x: -0.5,
              y: -0.5,
              offsetY: -16,
              cursorStyle: 'pointer',
              mouseUpHandler: (eventData, transform) => {
                fabricCanvas.fire('control:clicked', {
                  controlName: 'moveToFront',
                  target: transform.target,
                  e: eventData,
                })
                moveToFront(eventData, transform)
                disableResetCounter.value++
                fabricCanvas.fire('object:modified', {
                  target: transform.target,
                  e: eventData,
                })
              },
              render: renderChevronIcon('up'),
            })
            obj.controls.moveToBottomControl = new Control({
              x: -0.5,
              y: -0.5,
              offsetY: -16,
              offsetX: 22,
              cursorStyle: 'pointer',
              mouseUpHandler: (eventData, transform) => {
                fabricCanvas.fire('control:clicked', {
                  controlName: 'moveToBottom',
                  target: transform.target,
                  e: eventData,
                })
                moveToBottom(eventData, transform)
                disableResetCounter.value++
                fabricCanvas.fire('object:modified', {
                  target: transform.target,
                  e: eventData,
                })
              },
              render: renderChevronIcon('down'),
            })
          }
        })
        fabricCanvas
          .getObjects()
          .forEach((obj: FabricImage & { layerId?: string; layerName: string }) => {
            if (obj.layerName?.toLowerCase().includes('product')) {
              const productId = obj.layerId
              if (productId) {
                const shadow = fabricCanvas
                  .getObjects()
                  .find(
                    (o: { layerName: string; layerId: string | undefined }) =>
                      (o.layerName === 'Shadow' || o.layerName == layerNames.AIShadow) && o.layerId === productId
                  )
                if (shadow) {
                  syncContactShadow(obj, shadow, fabricCanvas)
                }
              }
            }
          })
        let lastHoveredCorner = ''
        const setTooltipPosition = (pointer: { x: number; y: number }, offsetX: number) => {
          const canvasContainer = document.getElementsByClassName('canvas-container')[0]
          const canvasContainerRect = canvasContainer.getBoundingClientRect()
          const mainContent = document.getElementsByClassName('main-content')[0]
          const mainContentRect = mainContent.getBoundingClientRect()
          const header = document.getElementsByClassName('custom-header')[0]
          const headerRect = header.getBoundingClientRect()
          const mainContentRow = document.getElementsByClassName('main-content-row')[0]
          const mainContentRowRect = mainContentRow.getBoundingClientRect()
          const positionTop =
            (mainContentRect.height - canvasContainerRect.height) / 2 +
            headerRect.height +
            pointer.y * canvasZoom.value
          const positionLeft =
            (mainContentRect.width - mainContentRowRect.width) / 2 + pointer.x * canvasZoom.value
          tooltip.style.left = `${positionLeft + offsetX}px`
          tooltip.style.top = `${positionTop - 40}px`
        }
        fabricCanvas.on('mouse:over', (e: any) => {
          const target = e.target
          if (target) {
            const pointer = fabricCanvas.getPointer(e.e)
            if (
              target.__corner === 'moveToFrontControl' &&
              lastHoveredCorner !== 'moveToFrontControl'
            ) {
              tooltip.textContent = 'Move to Front'
              setTooltipPosition(pointer, -30)
              tooltip.style.display = 'block'
              lastHoveredCorner = 'moveToFrontControl'
            } else if (
              target.__corner === 'moveToBottomControl' &&
              lastHoveredCorner !== 'moveToBottomControl'
            ) {
              tooltip.textContent = 'Move to Back'
              setTooltipPosition(pointer, -36)
              tooltip.style.display = 'block'
              lastHoveredCorner = 'moveToBottomControl'
            }
          }
        })
        fabricCanvas.on('mouse:out', (e: any) => {
          const target = e.target
          if (target && lastHoveredCorner) {
            tooltip.style.display = 'none'
            lastHoveredCorner = ''
          }
        })
        if (
          editorStore.selectedBackgroundOption === 0 &&
          editorStore.selectedBackGroundIndex != null
        ) {
          const background = editorStore.createdBackGrounds[editorStore.selectedBackGroundIndex]
          await showGlowLayer(
            fabricCanvas,
            brandStore.selectedLogo?.logoUrl,
            brandStore.selectedColor,
            background,
          )
        } else if (editorStore.selectedBackDropIndex != null) {
          const background = editorStore.createdBackDrops[editorStore.selectedBackDropIndex]
          await showGlowLayer(
            fabricCanvas,
            brandStore.selectedLogo?.logoUrl,
            brandStore.selectedColor,
            background,
          )
        } else if (editorStore.isAutoFix) {
          await showGlowLayer(
            fabricCanvas,
            brandStore.selectedLogo?.logoUrl,
            brandStore.selectedColor,
          )
        }
        if (placementStore.selectedBackgroundImage == specialBackgrounds[0]) {
          await addDuoToneBackground(
            fabricCanvas,
            backDrops.value[editorStore.selectedBackDropIndex ?? 0].backgroundColor,
            specialBackgrounds[1],
          )
        }
        fabricCanvas?.renderAll()

        if (assetStore.startCanvasUpload) {
          setTimeout(async () => {
            try {
              const s3Url = await uploadFabricCanvasToS3(fabricCanvas)
              assetStore.setCreativeS3Url(s3Url)
              assetStore.setStartCanvasUpload(false)
              logCreative(false)
            } catch (err) {
              console.log('Upload failed', err)
            }
          }, 0)
        }
      })
      .catch(() => {
        canvasStore.setCanvasError(true)
        notify('Failed to load canvas!', 'error')
      })
      .finally(() => {
        disableResetCounter.value++
        canvasStore.setCanvasLoader(false)
        canvasStore.setHideLoader(false)
        resolve()
      })
  })
}

const loadNewCanvasData = () => {
  const canvasJson = canvasStore.canvasJson
  const headlineObj = canvasJson.objects.find((obj: any) => obj.layerName === 'Headline')
  const subheadlineObj = canvasJson.objects.find((obj: any) => obj.layerName === 'SubHeadline')
  if (!subheadlineObj) {
    showSubheadline.value = false
  }
  editorStore.setHeadline(headlineObj?.text || '')
  headlineFontSize.value = headlineObj?.fontSize || 72
  editorStore.setSubheadline(subheadlineObj?.text || '')
  subheadlineFontSize.value = subheadlineObj?.fontSize || 48
}

// Enhancement functions
const addEnhancement = () => {
  if (newEnhancement.value.trim()) {
    const items = enhancingItems.value
    const key = `${selectedColor.value}_${placementStore.selectedBackgroundImage}`
    items.push({
      name: newEnhancement.value.trim(),
      apply: {},
      remove: { [key]: canvasStore.canvasJson.backgroundImage?.src || '' },
    })
    enhancementsStore.setEnhancingItems(items)
    newEnhancement.value = ''
    showAddEnhancement.value = false
    selectedItem.value = enhancingItems.value.length - 1
  }
}

const clearEnhancement = () => {
  newEnhancement.value = ''
  showAddEnhancement.value = false
}

const enhanceCreative = async (changeBackground: boolean = false, oldSelectedValue: any = null) => {
  try {
    const textColor = canvasStore.textColor
    enhanceLoading.value = true
    canvasStore.setCanvasLoader(true)
    canvasStore.setHideLoader(false)
    canvasStore.setCanvasError(false)

    const key = `${selectedColor.value}`

    const updatedItems = enhancingItems.value.map((item: EnhancingItem) => ({
      ...item,
      remove: { ...item.remove, [key]: canvasStore.backgroundImageUrl || '' },
    }))
    
    const name = (selectedItem.value != null && enhancingItems.value[selectedItem.value].name) || ''
    const item = enhancingItems.value.find((item: EnhancingItem) => item.name == name)
    const backgroundResponse = await creativeService.addEnhancedBackground({
      ...enhancementPrompt.value,
    })
    const backgroundUrl = backgroundResponse.result_image_url
    if (name && item) {
      item.apply[key] = backgroundUrl
      enhancementsStore.updateEnhancingItem(item)
    }
    if (changeBackground) {
      if (
        editorStore.selectedBackgroundOption === 0 &&
        editorStore.selectedBackGroundIndex != null
      ) {
        const background = editorStore.createdBackGrounds[editorStore.selectedBackGroundIndex]
        await editorService.addBackgroundImage(canvasStore.fabricCanvas, backgroundUrl)
        await editorService.getGlowOfLogo(background)
      } else if (editorStore.selectedBackDropIndex != null) {
        const background = editorStore.createdBackDrops[editorStore.selectedBackDropIndex]
        await editorService.addBackgroundImage(canvasStore.fabricCanvas, backgroundUrl)
        await editorService.getGlowOfLogo(background)
      }
    }
    canvasStore.setBgImageUrl(backgroundUrl)
    await editorService.addBackgroundImage(fabricCanvas, backgroundUrl)
  } catch (error: any) {
    const e = getErrorMessage(error)
    notify(e?.message || 'Failed to enhance creative!', "error")
    if (selectedItem.value) {
      enhancingItems.value.splice(selectedItem.value, 1);
      selectedItem.value = oldSelectedValue;
    }
  } finally {
    enhanceLoading.value = false
    canvasStore.setCanvasLoader(false)
  }
}

// Logo functions
const updateCanvasJsonLogo = async (logo: Logo) => {
  await undoRedoService.updateLogoWithUndo(logo)
}

const changeRandomShadow = async (chooseRandom: boolean = true) => {
  editorStore.setIsShufflingPlacement(true)
  await undoRedoService.changeRandomShadowWithUndo(chooseRandom)
  editorStore.setIsShufflingPlacement(false)
  canvasStore.fabricCanvas.renderAll()
}

// Discount functions
const getCurrentDiscountInfo = () => ({
  type: canvasStore.discountType,
  variation: canvasStore.discountVariation,
  value: canvasStore.discountValue,
  couponCode: canvasStore.rawCouponCode?.toUpperCase(),
})

const updateDiscountChanges = async () => {
  if (isDiscountFieldValid.value === false) {
    notify(t('errors.pleaseEnterValidDiscount'), 'error')
    return
  }
  const previousDiscountInfo = canvasStore.lastUsedDiscountInfo
  isLoadingHeadlinesUsingDiscount.value = true
  canvasStore.setDiscountType(tempDiscountDetails.value.type)
  canvasStore.setDiscountVariation(tempDiscountDetails.value.variation)
  canvasStore.setDiscountValue(tempDiscountDetails.value.value)
  canvasStore.setRawCouponCode(tempDiscountDetails.value.couponCode)
  const currentDiscountInfo = getCurrentDiscountInfo()
  try {
    await undoRedoService.updateDiscountChangesWithUndo(previousDiscountInfo, currentDiscountInfo)
    canvasStore.setLastUsedDiscountInfo(currentDiscountInfo)
  } catch {
    notify('Failed to update discount information!', 'error')
  } finally {
    isLoadingHeadlinesUsingDiscount.value = false
    closeDiscountMenu()
  }
}

const onDiscountMenuToggle = (isOpen: boolean) => {
  if (isOpen) {
    tempDiscountDetails.value = {
      type: canvasStore.discountType,
      variation: canvasStore.discountVariation,
      value: canvasStore.discountValue,
      couponCode: canvasStore.rawCouponCode?.toUpperCase(),
    }
  }
}

const closeDiscountMenu = () => {
  discountMenu.value = false
}

const updateHeadline = (newHeadline: string, textTypeName: TextTypeName, noUndo: boolean = false) => {
  if (newHeadline && USkeywords.some((word) => newHeadline.toLowerCase().includes(word))) {
    newHeadline = handleUSToUKEnglish(newHeadline)
  }
  if (textTypeName === TEXT_TYPE_NAMES.HEADLINE) {
    editorStore.setHeadline(newHeadline)
    editorStore.setHeadlinePromptMenu(false)
    if (!noUndo) {
      undoRedoService.updateTextWithUndo(newHeadline, textTypeName)
    } else {
      editorService.updateCanvasJsonText(newHeadline, textTypeName)
    }
  } else if (textTypeName === TEXT_TYPE_NAMES.SUBHEADLINE) {
    editorStore.setSubheadline(newHeadline)
    editorStore.setSubheadlinePromptMenu(false)
    if (!noUndo) {
      undoRedoService.updateTextWithUndo(newHeadline, textTypeName)
    } else {
      editorService.updateCanvasJsonText(newHeadline, textTypeName)
    }
  }
}

// Utility functions
const blockNegativeSign = (e: KeyboardEvent) => {
  if (e.key === '-' || e.key == '+' || e.key == 'e' || e.key == 'E') {
    e.preventDefault()
    notify('Non Numberical values are not allowed', 'error')
  }
}

const onImageError = () => {
  showPlaceholder.value = true
}

const onImageLoad = () => {
  showPlaceholder.value = false
}

function getNonRepeatingRandom(min: number, max: number): number {
  let index
  do {
    index = Math.floor(Math.random() * (max - min + 1)) + min
  } while (lastRandomIndex.includes(index) && max - min > 0) // avoid infinite loop
  lastRandomIndex.push(index)
  if (lastRandomIndex.length === max - min) {
    lastRandomIndex = []
  }
  return index
}

// Create debounced versions of updateTextWithUndo
const debouncedUpdateHeadlineText = debounce((text: string) => {
  undoRedoService.updateTextWithUndo(text, 'Headline')
}, 500)

const debouncedUpdateSubheadlineText = debounce((text: string) => {
  undoRedoService.updateTextWithUndo(text, 'SubHeadline')
}, 500)

const writeHeadlineUsingAI = async (prompt: string) => {
  await editorService.writeUsingAI(prompt, TEXT_TYPE_NAMES.HEADLINE)
  headlinePrompt.value = ''
}

const writeSubHeadlineUsingAI = async (prompt: string) => {
  await editorService.writeUsingAI(prompt, TEXT_TYPE_NAMES.SUBHEADLINE)
  subHeadlinePrompt.value = ''
}

const selectNewOverlay = async (index: number) => {
  const overlayUrl = autofixOverlays[index]
  if (overlayUrl === placementStore.selectedOverlayImage) {
    return
  }

  await undoRedoService.selectNewOverlayWithUndo(index)
}

const selectNewTextColor = async (index: number) => {
  if (canvasStore.textColor === autofixStore.autofixColors[index]) {
    return
  }
  undoRedoService.selectNewTextColorWithUndo(index)
}

const addNewTextColor = () => {
  if (autofixStore.autofixColors.includes(newTextColor.value.toLowerCase())) {
    alreadyPresent.value = true
    return
  }
  alreadyPresent.value = false
  undoRedoService.addTextColorWithUndo(newTextColor.value)
  textMenu.value = false
  newTextColor.value = '#ffffff'
  selectedTextColorCode.value = colorCodes.HEX
}

const closeTextMenu = () => {
  textMenu.value = false
  alreadyPresent.value = false
  newTextColor.value = '#ffffff'
  selectedTextColorCode.value = colorCodes.HEX
}

const textColor = computed({
  get: () => {
    if (selectedTextColorCode.value === colorCodes.HEX) {
      return newTextColor.value.slice(1).toUpperCase()
    } else {
      return newTextColor.value
    }
  },
  set: (color: string) => {
    if (selectedTextColorCode.value === colorCodes.HEX) {
      newTextColor.value = '#' + color
    } else {
      newTextColor.value = color
    }
  },
})

function getOutlineColor(color: string) {
  let r, g, b
  // let color = brandStore.selectedColor
  if (color.startsWith('#')) {
    color = color.replace('#', '')
    if (color.length === 3) {
      color = color
        .split('')
        .map((ch) => ch + ch)
        .join('')
    }
    r = parseInt(color.substring(0, 2), 16)
    g = parseInt(color.substring(2, 4), 16)
    b = parseInt(color.substring(4, 6), 16)
  } else if (color.startsWith('rgb')) {
    const rgbValues = color.match(/\d+(\.\d+)?/g)?.map(Number) || [0, 0, 0]
    ;[r, g, b] = rgbValues
  } else {
    return 'black'
  }

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  return luminance > 0.5 ? 'black' : 'white'
}
</script>

<style scoped>
.headline_outputs {
  letter-spacing: normal !important;
}

.header {
  z-index: 1999;
}

.user-colors {
  overflow-y: scroll;
}

.user-colors::-webkit-scrollbar {
  display: none;
}

:deep(.product-badge .v-badge__badge) {
  border-top-left-radius: 7.5px !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 2px !important;
  border-bottom-left-radius: 0 !important;
  font-weight: bold;
}

:deep(.headline-field .v-field--appended) {
  padding-right: 0 !important;
}

:deep(.v-messages__message) {
  display: flex;
  flex-direction: column;
  align-items: start;
}

:deep(.v-text-field .v-input__details) {
  padding-inline: 0px 20px !important;
  padding-bottom: 6px !important;
}
:deep(.warning-state .v-field--error .v-field__outline) {
  color: black !important;
}

.column__padding {
  padding-left: 19px !important;
}
.card_action__class {
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 1;
}

:deep(.color-picker__class .v-color-picker-preview__sliders) {
  display: none;
}

:deep(.color-picker__menu .v-color-picker-preview__dot) {
  display: none;
}

:deep(.color-picker__menu .v-color-picker-preview__eye-dropper) {
  display: none;
}

:deep(.v-color-picker-preview__alpha) {
  display: none;
}

:deep(.color-picker__class .v-color-picker__controls) {
  padding-left: 2px;
  padding-right: 0;
  padding-top: 0;
  padding-bottom: 0px;
}

.color-picker__class {
  max-width: 50px !important;
}
</style>
